<template>
  <!-- Shared frame of the signed-out pages (sign in, register, forgot / reset password): a world map of
       where proxies are sold on the left, the form (default slot) on the right. The map and the three
       counters come from the public stock summary; if that call fails the page still works without them. -->
  <div class="auth">
    <div class="auth__split">
      <section class="auth__hero">
        <svg class="auth__map" :viewBox="`0 0 ${WORLD_W} ${WORLD_H}`" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <g>
            <path v-for="c in COUNTRIES" :key="c.n" :d="c.d" class="auth__land" :class="{ 'is-lit': lit.has(c.n) }" />
          </g>
          <g>
            <template v-for="p in dots" :key="p.key">
              <circle v-if="p.in_stock" class="auth__halo" :cx="p.x" :cy="p.y" r="7" />
              <circle class="auth__dot" :class="{ 'is-idle': !p.in_stock }" :cx="p.x" :cy="p.y" r="2.5" />
            </template>
          </g>
        </svg>
        <div class="auth__overlay">
          <div class="auth__brand"><span class="auth__accent">◉</span> {{ brand[0] }}<span class="auth__accent">{{ brand[1] }}</span></div>
          <h1>{{ t('auth.heroTitle1') }}<br />{{ t('auth.heroTitle2') }}</h1>
          <p class="auth__sub">{{ t('auth.heroSub') }}</p>
          <div class="auth__stats">
            <template v-if="summary">
              <div><div class="auth__n">{{ summary.in_stock }}</div><div class="auth__label">{{ t('auth.statInStock') }}</div></div>
              <div><div class="auth__n">{{ summary.countries }}</div><div class="auth__label">{{ t('auth.statCountries') }}</div></div>
              <div><div class="auth__n">{{ summary.cities }}</div><div class="auth__label">{{ t('auth.statCities') }}</div></div>
            </template>
          </div>
        </div>
      </section>
      <section class="auth__panel">
        <div v-if="!hideLang" class="auth__lang"><LanguageSwitch variant="auth" /></div>
        <div class="auth__card" :class="{ 'is-wide': wide }">
          <slot />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { t } from '@/i18n';
import { ref, computed, onMounted, onBeforeUnmount, defineProps } from 'vue';   // the import keeps this project's lint (no-undef) happy
import { WORLD_W, WORLD_H, COUNTRIES, project } from '@/assets/worldMap.js';
import LanguageSwitch from '@/components/Layouts/LanguageSwitch.vue';

defineProps({ wide: { type: Boolean, default: false }, hideLang: { type: Boolean, default: false } });   // hideLang: the page has its own language field   // register has two columns of fields

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;
const brand = process.env.VUE_APP_VUE_NAME === 'proxywarehouse' ? ['Proxy', 'Warehouse'] : process.env.VUE_APP_VUE_NAME === 'proproxy' || !process.env.VUE_APP_VUE_NAME ? ['Pro', 'Proxies'] : ['Proxy', 'Dashboard'];

// the map names a few countries differently from the dashboard
const MAP_NAME = { 'United States': 'United States of America', 'USA': 'United States of America', 'UK': 'United Kingdom', 'Czech Republic': 'Czechia' };

const summary = ref(null);
const lit = computed(() => new Set((summary.value?.stocked_countries || []).map((n) => MAP_NAME[n] || n)));
const dots = computed(() => (summary.value?.points || []).map((p) => {
  const [x, y] = project(p.lat, p.lng);
  return { key: `${p.country}|${p.city}`, in_stock: Boolean(p.in_stock), x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
}).sort((a, b) => Number(a.in_stock) - Number(b.in_stock)));   // stocked dots are drawn on top

const loadSummary = async () => {
  try {
    const res = await fetch(`${baseUrl}/public/stock-summary`);
    if (res.ok) summary.value = await res.json();
  } catch (e) { /* the page works without the counters */ }
};
let timer = null;
// first load always; then every 60 s while the tab is visible (the server caches for 60 s too), and on return to the tab
const onVisible = () => { if (!document.hidden) loadSummary(); };
onMounted(() => {
  loadSummary();
  timer = setInterval(() => { if (!document.hidden) loadSummary(); }, 60000);
  document.addEventListener('visibilitychange', onVisible);
});
onBeforeUnmount(() => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); });
</script>

<style>
/* not scoped on purpose: the forms placed in the slot use the .auth-* field and button classes below */
.auth {
  --au-bg: #0f1420; --au-card: #171d2e; --au-card-2: #1c2338; --au-line: #2a3350; --au-text: #e8ecf6; --au-muted: #8b95b0;
  --au-accent: #3b82f6; --au-green: #22c55e; --au-red: #ef4444; --au-land: #232c46; --au-land-stock: #2e4a7a; --au-ocean: #10162a;
  min-height: 100vh; background: var(--au-bg); color: var(--au-text);
  font-family: "Segoe UI", system-ui, -apple-system, Roboto, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans SC", sans-serif; font-size: 14px;
}
.auth__split { display: grid; grid-template-columns: 1.5fr 1fr; min-height: 100vh; }
.auth__hero { position: relative; background: var(--au-ocean); overflow: hidden; }
.auth__map { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
.auth__land { fill: var(--au-land); stroke: var(--au-line); stroke-width: 0.5; }
.auth__land.is-lit { fill: var(--au-land-stock); }
.auth__dot { fill: var(--au-green); }
.auth__dot.is-idle { fill: var(--au-muted); opacity: 0.55; }
.auth__halo { fill: var(--au-green); opacity: 0.25; transform-box: fill-box; transform-origin: center; animation: auth-pulse 2.5s ease-in-out infinite; }
@keyframes auth-pulse { 50% { transform: scale(1.7); opacity: 0.08; } }
@media (prefers-reduced-motion: reduce) { .auth__halo { animation: none; } }
.auth__overlay { position: relative; z-index: 1; height: 100%; min-height: 100%; display: flex; flex-direction: column; justify-content: flex-end; padding: 40px 48px;
  background: linear-gradient(rgba(16, 22, 42, 0.55), rgba(16, 22, 42, 0.05) 40%, rgba(16, 22, 42, 0.75)); pointer-events: none; }
.auth__brand { position: absolute; top: 28px; left: 48px; font-size: 20px; font-weight: 700; letter-spacing: 0.5px; color: var(--au-text); }
.auth__accent { color: var(--au-accent); }
.auth__overlay h1 { font-size: 32px; line-height: 1.2; margin: 0 0 10px; color: var(--au-text); font-weight: 700; }
.auth__sub { color: var(--au-muted); font-size: 15px; margin: 0 0 26px; }
.auth__stats { display: flex; gap: 36px; min-height: 58px; }
.auth__n { font-size: 28px; font-weight: 700; color: var(--au-green); font-variant-numeric: tabular-nums; line-height: 1.2; }
.auth__label { color: var(--au-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 1px; }
.auth__panel { position: relative; display: flex; align-items: center; justify-content: center; background: var(--au-card); border-left: 1px solid var(--au-line); padding: 24px; }
.auth__lang { position: absolute; top: 18px; right: 20px; }
.auth__card { width: 320px; max-width: 100%; }
.auth__card.is-wide { width: 440px; }

.auth-title { font-size: 22px; margin: 0 0 4px; color: var(--au-text); font-weight: 700; }
.auth-sub { color: var(--au-muted); font-size: 13px; margin: 0 0 24px; }
.auth-field { display: block; margin-bottom: 16px; }
.auth-field > span.auth-field__label { display: block; font-size: 12px; color: var(--au-muted); margin-bottom: 6px; }
.auth-field input, .auth-field select { width: 100%; background: var(--au-card-2); border: 1px solid var(--au-line); border-radius: 8px; color: var(--au-text); padding: 10px 12px; font-size: 14px; outline: none; box-shadow: none; }
.auth-field input::placeholder { color: #5f6985; }
.auth-field input:focus, .auth-field select:focus { border-color: var(--au-accent); }
.auth-field select option { color: #111827; }
/* browser autofill paints its own light background. The site-wide "input:focus { box-shadow: none !important }" would
   remove the usual inset-shadow mask while typing, so this rule is !important too and the background change is delayed */
.auth .auth-field input:-webkit-autofill, .auth .auth-field input:-webkit-autofill:hover, .auth .auth-field input:-webkit-autofill:focus, .auth .auth-field input:-webkit-autofill:active {
  -webkit-text-fill-color: var(--au-text) !important; caret-color: var(--au-text);
  box-shadow: 0 0 0 1000px var(--au-card-2) inset !important; -webkit-box-shadow: 0 0 0 1000px var(--au-card-2) inset !important;
  transition: background-color 999999s ease-in-out 0s; background-clip: padding-box; }
.auth .auth-field input { color: var(--au-text) !important; background-color: var(--au-card-2) !important; }
.auth-error { display: block; color: #fca5a5; font-size: 12px; margin-top: 4px; }
/* the reCAPTCHA frame is 400px tall with an invisible lower part; unclipped it sits over the button below and eats the clicks */
.auth-captcha { position: relative; z-index: 0; margin: 0 0 16px; height: 78px; overflow: hidden; }
.auth-captcha.is-error { outline: 1px solid var(--au-red); border-radius: 4px; width: 304px; max-width: 100%; }
.auth-btn { position: relative; z-index: 2; display: block; width: 100%; padding: 11px; border: 1px solid var(--au-accent); border-radius: 8px; background: var(--au-accent); color: #ffffff; font-size: 14px; font-weight: 600; line-height: 1.2; cursor: pointer; }
.auth-btn:hover { filter: brightness(1.15); }
.auth-btn:disabled { opacity: 0.6; cursor: default; }
.auth-links { position: relative; z-index: 2; display: flex; justify-content: space-between; gap: 12px; margin-top: 18px; font-size: 13px; }
.auth-links a { color: var(--au-muted); text-decoration: none; }
.auth-links a:hover { color: var(--au-accent); }
.auth-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 14px; }

@media (max-width: 900px) {
  .auth__split { grid-template-columns: minmax(0, 1fr); grid-template-rows: 42vh auto; }
  .auth__overlay { padding: 24px; }
  .auth__brand { left: 24px; top: 20px; }
  .auth__overlay h1 { font-size: 24px; }
  .auth__sub { font-size: 13px; margin-bottom: 14px; }
  .auth__stats { gap: 22px; min-height: 0; }
  .auth__n { font-size: 22px; }
  .auth__panel { border-left: 0; border-top: 1px solid var(--au-line); padding: 32px 20px 48px; align-items: flex-start; }
  .auth-grid { grid-template-columns: minmax(0, 1fr); }
}
</style>
